import { ReactElement,useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import styled from '@emotion/styled';

import { AnimationComponentRef, useThemeControl } from 'brand/hooks';

import { AtlasEntry, CloudSection, CloudSectionProps } from './CloudSection';
import { ContentsSection } from './ContentsSection';
import { EventSection } from './EventSection';
import { HeroSection } from './HeroSection';
import { LifeEventSection } from './LifeEventSection/LifeEventSection';
import { BREAK_POINT } from '@bucketplace/design-system';
import { CLOUD_SECTION_ASSETS_URL } from './constants';
import { FooterContainer } from 'brand/common/RebrandFooterContainer';
import { usePlatformInfo } from 'promotions/hooks/usePlatformInfo';

type RebrandContentProps = CloudSectionProps;

export function RebrandContent({
  mobilePositions: initialMobilePositions,
  pcPositions: initialPcPositions,
}: RebrandContentProps): ReactElement {
  // ISR로 받은 데이터를 기본값으로 사용
  const [mobilePositions, setMobilePositions] = useState<AtlasEntry[]>(initialMobilePositions);
  const [pcPositions, setPcPositions] = useState<AtlasEntry[]>(initialPcPositions);
  const containerRef = useRef<HTMLDivElement>(null);
  const pinSectionRef = useRef<HTMLDivElement>(null);
  const flowSectionRef = useRef<HTMLDivElement>(null);
  const fixedSectionRef = useRef<HTMLDivElement>(null);
  const endSectionRef = useRef<HTMLDivElement>(null);
  const footerSectionRef = useRef<HTMLDivElement>(null);

  const cloudSectionRef = useRef<AnimationComponentRef>(null);
  const heroSectionRef = useRef<AnimationComponentRef>(null);
  const lifeEventSectionRef = useRef<AnimationComponentRef>(null);
  const contentsSectionRef = useRef<AnimationComponentRef>(null);
  const eventSectionRef = useRef<AnimationComponentRef>(null);
  const prefetchLinksRef = useRef<HTMLLinkElement[]>([]);
  const footerRef = useRef<AnimationComponentRef>(null);

  const { osType } = usePlatformInfo();
  const isMobileDevice = osType !== 'Web';

  useEffect(() => {
    // ISR 데이터가 없는 경우, 다시 fetch
    if (initialMobilePositions.length === 0 || initialPcPositions.length === 0) {
      const fetchPositions = async (): Promise<void> => {
        if (initialMobilePositions.length === 0) {
          fetch(CLOUD_SECTION_ASSETS_URL.positions.mobile)
            .then((res) => res.json())
            .then((data) => {
              setMobilePositions(data);
            })
            .catch((err) => console.error('Mobile error:', err));
        }

        if (initialPcPositions.length === 0) {
          fetch(CLOUD_SECTION_ASSETS_URL.positions.pc)
            .then((res) => res.json())
            .then((data) => {
              setPcPositions(data);
            })
            .catch((err) => console.error('PC error:', err));
        }
      };

      fetchPositions();
    }
  }, [initialMobilePositions, initialPcPositions]);


  // 아틀라스 이미지 prefetch
  useEffect(() => {
    const prefetchAtlasImages = (): void => {
      // 이전 prefetch links 정리
      prefetchLinksRef.current.forEach(link => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      });
      prefetchLinksRef.current = [];

      const isMobile = window.innerWidth < BREAK_POINT.laptop;
      const deviceType = isMobile ? 'mobile' : 'pc';

      // CDN URL에서 직접 prefetch
      const priorityUrls = CLOUD_SECTION_ASSETS_URL.atlas[deviceType].priority;
      const normalUrls = CLOUD_SECTION_ASSETS_URL.atlas[deviceType].normal;

      // Priority atlas 먼저 prefetch
      priorityUrls.forEach((url) => {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = url;
        link.as = 'image';
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
        prefetchLinksRef.current.push(link);
      });

      // Normal atlas는 우선순위 낮게 prefetch (처음 몇 개만)
      normalUrls.slice(0, 3).forEach((url) => {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = url;
        link.as = 'image';
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
        prefetchLinksRef.current.push(link);
      });
    };

    // position 데이터가 있을 때만 실행
    if (mobilePositions.length > 0 || pcPositions.length > 0) {
      prefetchAtlasImages();
    }

    return () => {
      // cleanup: 추적된 link들만 제거
      prefetchLinksRef.current.forEach(link => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      });
      prefetchLinksRef.current = [];
    };
  }, [mobilePositions, pcPositions]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    gsap.registerPlugin(ScrollTrigger);

    if (isMobileDevice) {
      ScrollTrigger.normalizeScroll({
        allowNestedScroll: false,
        lockAxis: false,
        momentum: 0, // 관성 제거로 정확한 멈춤
        type: 'touch,wheel,pointer',
      });
    }

    // 섹션 전환 시 부드러운 멈춤을 위한 설정
    ScrollTrigger.config({
      limitCallbacks: true,
      syncInterval: 40,
    });

    const ctx = gsap.context(() => {
      // main 타임라인 생성
      // const mainTL = gsap.timeline({
      //   defaults: { ease: 'none' },
      // });

      // const mainTL = gsap.timeline();

      const pinTL = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: pinSectionRef.current,
          start: 'top top',
          end: '+=1600%',
          pin: true,
          scrub: 1,
          anticipatePin: 1,
          ...(isMobileDevice && {
            snap: {
              snapTo: [0, 0.2, 0.3, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
              duration: { min: 10 },
              delay: 2,
              ease: 'power2.out',
              directional: true,
            },
          }),
        },
      });

      const flowTL = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: flowSectionRef.current,
          start: 'top top',
          end: '+=600%',
          pin: true,
          scrub: 1,
          ...(isMobileDevice && {
            snap: {
              snapTo: [0, 0.2, 0.4, 0.6, 0.8, 1],
              duration: { min: 10 },
              delay: 2,
              ease: 'power2.out',
              directional: true,
            },
          }),
        },
      });

      const fixedTL = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: fixedSectionRef.current,
          start: 'top top',
          end: '+=600%',
          scrub: 1,
          pin: true,
          ...(isMobileDevice && {
            snap: {
              snapTo: [0, 0.2, 0.4, 0.6, 0.8, 1],
              duration: { min: 10 },
              delay: 2,
              ease: 'power2.out',
              directional: true,
            },
          }),
        },
      });

      const endTL = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: endSectionRef.current,
          start: 'top top',
          end: '+=200%',
          scrub: 1,
          pin: true,
          ...(isMobileDevice && {
            snap: {
              snapTo: 1,
              duration: { min: 3 },
              delay: 0.2,
              ease: 'power2.out',
            },
          }),
        },
      });

      const footerTL = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: endSectionRef.current,
          start: 'top top',
          end: 'bottom bottom',
          pin: false,
        },
      });

      // children timeline 생성
      const cloudSectionTL = cloudSectionRef.current?.createTimeline();
      const heroSectionTL = heroSectionRef.current?.createTimeline();
      const lifeEventSectionTL = lifeEventSectionRef.current?.createTimeline();
      const contentsSectionTL = contentsSectionRef.current?.createTimeline();
      const eventSectionTL = eventSectionRef.current?.createTimeline();
      const footerSectionTL = footerRef.current?.createTimeline();

      pinTL
        .add(cloudSectionTL, 0)
        .add(heroSectionTL, '>')
        .to({}, { duration: 0.5 }, '>');

      flowTL
        .add(lifeEventSectionTL, 0)
        .to({}, { duration: 0.3 }, '>');

      fixedTL
        .add(contentsSectionTL, 0)
        .to({}, { duration: 0.2 }, '>');

      endTL.add(eventSectionTL, 0);

      footerTL.add(footerSectionTL, 0);
      // mainTL
      //   // 1단계: 카드들 순차 등장 (0초~2.4초)
      //   .add(pinTL, 0)
      //   .add(flowTL, '0%')
      //   .add(contentsSectionTL, '>');
    });

    // 컴포넌트 언마운트 시 정리
    return () => {
      ctx.revert();
      ScrollTrigger.normalizeScroll(false);
    };
  }, [isMobileDevice]);

  /** 테마 제어 */
  useThemeControl(containerRef);

  return (
    <div ref={containerRef}>
      <StagePinned ref={pinSectionRef}>
        <CloudSection ref={cloudSectionRef} mobilePositions={mobilePositions} pcPositions={pcPositions} />
        <HeroSection ref={heroSectionRef} />
      </StagePinned>
      <StageFlow ref={flowSectionRef}>
        <LifeEventSection ref={lifeEventSectionRef} />
      </StageFlow>
      <StageFlow ref={fixedSectionRef}>
        <ContentsSection ref={contentsSectionRef} />
      </StageFlow>
      <StageFlow ref={endSectionRef}>
        <EventSection ref={eventSectionRef} />
      </StageFlow>
      <StageFlow ref={footerSectionRef}>
        <FooterContainer ref={footerRef} />
      </StageFlow>
    </div>
  );
}

const StagePinned = styled.div`
  position: relative;
  height: 100vh;          /* 이 컨테이너 자체가 한 화면 */
  overflow: clip;

  > div {
    position: absolute;   /* 자식 섹션을 겹치게 */
    inset: 0;
    width: 100%;
    height: 100%;
  }
`;

/** life+contents: 일반 문서 흐름. 각 섹션이 자기 높이만큼 페이지를 채우며 스크롤됨 */
const StageFlow = styled.div`
  position: relative;
  /* 필요하면 배경색 등 스타일 추가 */
  
  > div {
    position: relative;   /* 절대 위치 금지: 자연 스크롤로 올라가게 */
    min-height: 100vh;    /* 각 섹션은 한 화면 이상 차지 */
    width: 100%;
  }
`;
